/**
 * StepConfigPanel — Right sidebar for editing a selected step's configuration.
 * Dynamic form based on step type.
 */

import React from 'react';
import {
  Box, Typography, TextField, Button, Divider, IconButton,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

export default function StepConfigPanel({ step, allSteps, onChange, onDelete, onClose }) {
  if (!step) {
    return (
      <Box sx={{ width: 280, p: 2, borderLeft: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Select a step to configure
        </Typography>
      </Box>
    );
  }

  function updateField(path, value) {
    const updated = { ...step };
    if (path === 'label') updated.label = value;
    else if (path === 'id') updated.id = value;
    else {
      // Nested config update
      updated.config = { ...(updated.config || {}) };
      const parts = path.split('.');
      if (parts.length === 1) {
        updated.config[parts[0]] = value;
      } else {
        // Two levels deep
        updated.config[parts[0]] = { ...(updated.config[parts[0]] || {}) };
        updated.config[parts[0]][parts[1]] = value;
      }
    }
    onChange(updated);
  }

  function updateNext(value) {
    onChange({ ...step, next: value ? [value] : [] });
  }

  const otherSteps = allSteps.filter(s => s.id !== step.id);

  return (
    <Box sx={{ width: 280, borderLeft: '1px solid', borderColor: 'divider', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, pb: 0 }}>
        <Typography variant="subtitle2">Step Configuration</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Common fields */}
        <TextField
          label="Step ID"
          value={step.id || ''}
          onChange={e => updateField('id', e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Label"
          value={step.label || ''}
          onChange={e => updateField('label', e.target.value)}
          size="small"
          fullWidth
        />

        <Typography variant="caption" color="text.secondary">
          Type: <strong>{step.type}</strong>
        </Typography>

        <FormControl size="small" fullWidth>
          <InputLabel>Next step</InputLabel>
          <Select
            value={step.next?.[0] || ''}
            label="Next step"
            onChange={e => updateNext(e.target.value)}
          >
            <MenuItem value="">(end of workflow)</MenuItem>
            {otherSteps.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.label || s.id}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider />

        {/* Type-specific config */}
        {step.type === 'validate' && (
          <TextField
            label="Rules (JSON)"
            value={JSON.stringify(step.config?.rules || [], null, 2)}
            onChange={e => {
              try { updateField('rules', JSON.parse(e.target.value)); } catch {}
            }}
            size="small"
            fullWidth
            multiline
            rows={4}
            sx={{ '& textarea': { fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.75rem' } }}
          />
        )}

        {step.type === 'preview' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Operation type</InputLabel>
            <Select
              value={step.config?.operation_type || ''}
              label="Operation type"
              onChange={e => updateField('operation_type', e.target.value)}
            >
              {['enroll', 'upgrade', 'downgrade', 'cancel', 'discount', 'rebundle', 'override'].map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {step.type === 'stripe_call' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Stripe operation</InputLabel>
            <Select
              value={step.config?.operation || ''}
              label="Stripe operation"
              onChange={e => updateField('operation', e.target.value)}
            >
              {['create_customer', 'create_subscription', 'cancel_subscription', 'update_plan',
                'issue_refund', 'apply_coupon', 'remove_coupon'].map(op => (
                <MenuItem key={op} value={op}>{op.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {step.type === 'db_mutation' && (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel>Operation</InputLabel>
              <Select
                value={step.config?.operation || ''}
                label="Operation"
                onChange={e => updateField('operation', e.target.value)}
              >
                <MenuItem value="insert">Insert</MenuItem>
                <MenuItem value="update">Update</MenuItem>
                <MenuItem value="delete">Delete</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Table"
              value={step.config?.table || ''}
              onChange={e => updateField('table', e.target.value)}
              size="small"
              fullWidth
            />
          </>
        )}

        {step.type === 'audit' && (
          <>
            <TextField
              label="Action name"
              value={step.config?.action || ''}
              onChange={e => updateField('action', e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Entity type"
              value={step.config?.entity_type || ''}
              onChange={e => updateField('entity_type', e.target.value)}
              size="small"
              fullWidth
            />
          </>
        )}

        {step.type === 'notify' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Notify type</InputLabel>
            <Select
              value={step.config?.notify_type || 'both'}
              label="Notify type"
              onChange={e => updateField('notify_type', e.target.value)}
            >
              <MenuItem value="product">Product webhook</MenuItem>
              <MenuItem value="user">User email</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
        )}

        {step.type === 'condition' && (
          <TextField
            label="Branches (JSON)"
            value={JSON.stringify(step.config?.branches || {}, null, 2)}
            onChange={e => {
              try { updateField('branches', JSON.parse(e.target.value)); } catch {}
            }}
            size="small"
            fullWidth
            multiline
            rows={4}
            sx={{ '& textarea': { fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.75rem' } }}
          />
        )}

        {step.type === 'schedule' && (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel>Action type</InputLabel>
              <Select
                value={step.config?.action_type || ''}
                label="Action type"
                onChange={e => updateField('action_type', e.target.value)}
              >
                <MenuItem value="revert">Revert</MenuItem>
                <MenuItem value="execute_step">Execute step</MenuItem>
                <MenuItem value="remove_discount">Remove discount</MenuItem>
                <MenuItem value="expire_override">Expire override</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Delay (hours)"
              type="number"
              value={step.config?.delay_hours || ''}
              onChange={e => updateField('delay_hours', parseInt(e.target.value) || 0)}
              size="small"
              fullWidth
            />
          </>
        )}

        {step.type === 'approve' && (
          <TextField
            label="Approval reason"
            value={step.config?.reason || ''}
            onChange={e => updateField('reason', e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        )}

        <Divider />

        <Button
          color="error"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(step.id)}
        >
          Delete Step
        </Button>
      </Box>
    </Box>
  );
}
